import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Animated,
} from 'react-native';
import {
  Text,
  Card,
  useTheme,
  Chip,
  IconButton,
  Surface,
  Button,
  Divider,
} from 'react-native-paper';
import type { MasteryState, ProgressInsight } from '@coffeeplexity/shared';
import { ConfidenceIndicator } from './ConfidenceIndicator';

export interface AIProgressInsight {
  id: string;
  type: 'improvement' | 'skill_gap' | 'efficiency' | 'trend' | 'prediction';
  title: string;
  description: string;
  aiReasoning: string;
  confidence: number;
  impact: 'low' | 'medium' | 'high';
  priority: number;
  actionable: boolean;
  recommendations: InsightRecommendation[];
  relatedSkills: string[];
  generatedAt: string;
  expiresAt?: string;
}

export interface InsightRecommendation {
  id: string;
  type: 'content' | 'practice' | 'schedule' | 'method';
  title: string;
  description: string;
  estimatedImpact: number;
  timeRequired: number;
  difficulty: number;
  contentId?: string;
  actionUrl?: string;
}

export interface SkillGapAnalysis {
  skillId: string;
  skillName: string;
  currentLevel: number;
  targetLevel: number;
  gap: number;
  priority: 'low' | 'medium' | 'high' | 'critical';
  blockedSkills: string[];
  recommendations: InsightRecommendation[];
  estimatedTimeToClose: number;
}

export interface LearningEfficiencyMetrics {
  overallEfficiency: number;
  timeOptimization: number;
  contentEngagement: number;
  retentionRate: number;
  practiceEffectiveness: number;
  suggestions: EfficiencySuggestion[];
}

export interface EfficiencySuggestion {
  id: string;
  category: 'timing' | 'content' | 'method' | 'frequency';
  title: string;
  description: string;
  potentialImprovement: number;
  implementationDifficulty: 'easy' | 'medium' | 'hard';
}

interface ProgressInsightsPanelProps {
  insights: AIProgressInsight[];
  skillGaps: SkillGapAnalysis[];
  efficiencyMetrics: LearningEfficiencyMetrics;
  masteryStates: MasteryState[];
  onInsightAction: (insightId: string, actionType: string) => void;
  onRecommendationSelect: (recommendation: InsightRecommendation) => void;
  onSkillGapFocus: (skillId: string) => void;
  style?: any;
}

type InsightFilter =
  | 'all'
  | 'improvement'
  | 'skill_gap'
  | 'efficiency'
  | 'trend';
type PriorityFilter = 'all' | 'high' | 'medium' | 'low';

export function ProgressInsightsPanel({
  insights,
  skillGaps,
  efficiencyMetrics,
  masteryStates,
  onInsightAction,
  onRecommendationSelect,
  onSkillGapFocus,
  style,
}: ProgressInsightsPanelProps) {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<
    'insights' | 'gaps' | 'efficiency'
  >('insights');
  const [insightFilter, setInsightFilter] = useState<InsightFilter>('all');
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all');
  const [expandedInsights, setExpandedInsights] = useState<Set<string>>(
    new Set()
  );

  // Filter insights based on current filters
  const filteredInsights = useMemo(() => {
    let filtered = insights;

    if (insightFilter !== 'all') {
      filtered = filtered.filter(insight => insight.type === insightFilter);
    }

    if (priorityFilter !== 'all') {
      filtered = filtered.filter(insight => insight.impact === priorityFilter);
    }

    // Sort by priority and confidence
    return filtered.sort((a, b) => {
      const priorityWeight = { high: 3, medium: 2, low: 1 };
      const aPriority = priorityWeight[a.impact] * a.confidence;
      const bPriority = priorityWeight[b.impact] * b.confidence;
      return bPriority - aPriority;
    });
  }, [insights, insightFilter, priorityFilter]);

  // Filter skill gaps by priority
  const prioritizedSkillGaps = useMemo(() => {
    return skillGaps
      .filter(
        gap => priorityFilter === 'all' || gap.priority === priorityFilter
      )
      .sort((a, b) => {
        const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      });
  }, [skillGaps, priorityFilter]);

  const handleInsightToggle = useCallback((insightId: string) => {
    setExpandedInsights(prev => {
      const newSet = new Set(prev);
      if (newSet.has(insightId)) {
        newSet.delete(insightId);
      } else {
        newSet.add(insightId);
      }
      return newSet;
    });
  }, []);

  const getInsightIcon = (type: AIProgressInsight['type']) => {
    switch (type) {
      case 'improvement':
        return 'trending-up';
      case 'skill_gap':
        return 'target';
      case 'efficiency':
        return 'speedometer';
      case 'trend':
        return 'chart-line';
      case 'prediction':
        return 'crystal-ball';
      default:
        return 'lightbulb';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high':
        return theme.colors.error;
      case 'medium':
        return theme.colors.primary;
      case 'low':
        return theme.colors.outline;
      default:
        return theme.colors.outline;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return '#D32F2F';
      case 'high':
        return '#F57C00';
      case 'medium':
        return '#1976D2';
      case 'low':
        return '#388E3C';
      default:
        return theme.colors.outline;
    }
  };

  const renderInsightCard = (insight: AIProgressInsight) => {
    const isExpanded = expandedInsights.has(insight.id);

    return (
      <Card key={insight.id} style={styles.insightCard}>
        <TouchableOpacity
          onPress={() => handleInsightToggle(insight.id)}
          style={styles.insightHeader}
        >
          <View style={styles.insightHeaderContent}>
            <View style={styles.insightInfo}>
              <View style={styles.insightTitleRow}>
                <IconButton
                  icon={getInsightIcon(insight.type)}
                  size={20}
                  iconColor={getImpactColor(insight.impact)}
                />
                <Text variant='titleMedium' style={styles.insightTitle}>
                  {insight.title}
                </Text>
                <ConfidenceIndicator
                  confidence={insight.confidence}
                  size='small'
                  showLabel={false}
                />
              </View>
              <Text variant='bodyMedium' style={styles.insightDescription}>
                {insight.description}
              </Text>
              <View style={styles.insightMeta}>
                <Chip
                  compact
                  style={[
                    styles.impactChip,
                    { backgroundColor: getImpactColor(insight.impact) + '20' },
                  ]}
                  textStyle={{ color: getImpactColor(insight.impact) }}
                >
                  {insight.impact.toUpperCase()} IMPACT
                </Chip>
                {insight.actionable && (
                  <Chip compact style={styles.actionableChip}>
                    ACTIONABLE
                  </Chip>
                )}
              </View>
            </View>
            <IconButton
              icon={isExpanded ? 'chevron-up' : 'chevron-down'}
              size={20}
            />
          </View>
        </TouchableOpacity>

        {isExpanded && (
          <View style={styles.insightDetails}>
            <Divider style={styles.divider} />

            {/* AI Reasoning */}
            <View style={styles.reasoningSection}>
              <Text variant='labelLarge' style={styles.sectionTitle}>
                AI Analysis
              </Text>
              <Text variant='bodyMedium' style={styles.reasoningText}>
                {insight.aiReasoning}
              </Text>
            </View>

            {/* Recommendations */}
            {insight.recommendations.length > 0 && (
              <View style={styles.recommendationsSection}>
                <Text variant='labelLarge' style={styles.sectionTitle}>
                  Recommended Actions
                </Text>
                {insight.recommendations.map(rec => (
                  <TouchableOpacity
                    key={rec.id}
                    style={styles.recommendationItem}
                    onPress={() => onRecommendationSelect(rec)}
                  >
                    <View style={styles.recommendationContent}>
                      <Text
                        variant='titleSmall'
                        style={styles.recommendationTitle}
                      >
                        {rec.title}
                      </Text>
                      <Text
                        variant='bodySmall'
                        style={styles.recommendationDescription}
                      >
                        {rec.description}
                      </Text>
                      <View style={styles.recommendationMeta}>
                        <Text variant='bodySmall' style={styles.metaText}>
                          Impact: +{Math.round(rec.estimatedImpact * 100)}%
                        </Text>
                        <Text variant='bodySmall' style={styles.metaText}>
                          Time: {rec.timeRequired}min
                        </Text>
                      </View>
                    </View>
                    <IconButton icon='chevron-right' size={16} />
                  </TouchableOpacity>
                ))}
              </View>
            )}

            {/* Action Buttons */}
            <View style={styles.actionButtons}>
              <Button
                mode='outlined'
                onPress={() => onInsightAction(insight.id, 'dismiss')}
                style={styles.actionButton}
              >
                Dismiss
              </Button>
              <Button
                mode='contained'
                onPress={() => onInsightAction(insight.id, 'apply')}
                style={styles.actionButton}
              >
                Apply Suggestions
              </Button>
            </View>
          </View>
        )}
      </Card>
    );
  };

  const renderSkillGapCard = (gap: SkillGapAnalysis) => (
    <Card key={gap.skillId} style={styles.gapCard}>
      <View style={styles.gapHeader}>
        <View style={styles.gapInfo}>
          <Text variant='titleMedium' style={styles.gapTitle}>
            {gap.skillName}
          </Text>
          <View style={styles.gapProgress}>
            <View style={styles.progressBar}>
              <View
                style={[
                  styles.progressFill,
                  {
                    width: `${gap.currentLevel * 100}%`,
                    backgroundColor: theme.colors.primary,
                  },
                ]}
              />
              <View
                style={[
                  styles.targetIndicator,
                  {
                    left: `${gap.targetLevel * 100}%`,
                    backgroundColor: getPriorityColor(gap.priority),
                  },
                ]}
              />
            </View>
            <Text variant='bodySmall' style={styles.progressText}>
              {Math.round(gap.currentLevel * 100)}% →{' '}
              {Math.round(gap.targetLevel * 100)}%
            </Text>
          </View>
          <View style={styles.gapMeta}>
            <Chip
              compact
              style={[
                styles.priorityChip,
                { backgroundColor: getPriorityColor(gap.priority) + '20' },
              ]}
              textStyle={{ color: getPriorityColor(gap.priority) }}
            >
              {gap.priority.toUpperCase()}
            </Chip>
            <Text variant='bodySmall' style={styles.timeEstimate}>
              ~{Math.round(gap.estimatedTimeToClose / 60)}h to close
            </Text>
          </View>
        </View>
        <Button
          mode='contained'
          onPress={() => onSkillGapFocus(gap.skillId)}
          style={styles.focusButton}
        >
          Focus
        </Button>
      </View>
    </Card>
  );

  const renderEfficiencyMetrics = () => (
    <View style={styles.efficiencyContainer}>
      {/* Overall Efficiency Score */}
      <Card style={styles.efficiencyCard}>
        <View style={styles.efficiencyHeader}>
          <Text variant='headlineSmall' style={styles.efficiencyScore}>
            {Math.round(efficiencyMetrics.overallEfficiency * 100)}%
          </Text>
          <Text variant='titleMedium'>Learning Efficiency</Text>
        </View>

        {/* Efficiency Breakdown */}
        <View style={styles.efficiencyBreakdown}>
          {[
            {
              label: 'Time Optimization',
              value: efficiencyMetrics.timeOptimization,
            },
            {
              label: 'Content Engagement',
              value: efficiencyMetrics.contentEngagement,
            },
            { label: 'Retention Rate', value: efficiencyMetrics.retentionRate },
            {
              label: 'Practice Effectiveness',
              value: efficiencyMetrics.practiceEffectiveness,
            },
          ].map(metric => (
            <View key={metric.label} style={styles.metricRow}>
              <Text variant='bodyMedium' style={styles.metricLabel}>
                {metric.label}
              </Text>
              <View style={styles.metricBar}>
                <View
                  style={[
                    styles.metricFill,
                    {
                      width: `${metric.value * 100}%`,
                      backgroundColor:
                        metric.value > 0.7
                          ? '#4CAF50'
                          : metric.value > 0.5
                            ? '#FF9800'
                            : '#F44336',
                    },
                  ]}
                />
              </View>
              <Text variant='bodySmall' style={styles.metricValue}>
                {Math.round(metric.value * 100)}%
              </Text>
            </View>
          ))}
        </View>
      </Card>

      {/* Efficiency Suggestions */}
      <View style={styles.suggestionsContainer}>
        <Text variant='titleMedium' style={styles.suggestionsTitle}>
          Optimization Suggestions
        </Text>
        {efficiencyMetrics.suggestions.map(suggestion => (
          <Card key={suggestion.id} style={styles.suggestionCard}>
            <View style={styles.suggestionContent}>
              <Text variant='titleSmall' style={styles.suggestionTitle}>
                {suggestion.title}
              </Text>
              <Text variant='bodyMedium' style={styles.suggestionDescription}>
                {suggestion.description}
              </Text>
              <View style={styles.suggestionMeta}>
                <Text variant='bodySmall' style={styles.improvementText}>
                  +{Math.round(suggestion.potentialImprovement * 100)}%
                  improvement
                </Text>
                <Chip
                  compact
                  style={[
                    styles.difficultyChip,
                    {
                      backgroundColor:
                        suggestion.implementationDifficulty === 'easy'
                          ? '#4CAF50'
                          : suggestion.implementationDifficulty === 'medium'
                            ? '#FF9800'
                            : '#F44336',
                    },
                  ]}
                >
                  {suggestion.implementationDifficulty}
                </Chip>
              </View>
            </View>
          </Card>
        ))}
      </View>
    </View>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'insights':
        return (
          <ScrollView style={styles.tabContent}>
            {filteredInsights.map(renderInsightCard)}
          </ScrollView>
        );
      case 'gaps':
        return (
          <ScrollView style={styles.tabContent}>
            {prioritizedSkillGaps.map(renderSkillGapCard)}
          </ScrollView>
        );
      case 'efficiency':
        return (
          <ScrollView style={styles.tabContent}>
            {renderEfficiencyMetrics()}
          </ScrollView>
        );
      default:
        return null;
    }
  };

  return (
    <Surface style={[styles.container, style]} elevation={1}>
      {/* Header */}
      <View style={styles.header}>
        <Text variant='headlineSmall' style={styles.title}>
          Progress Insights
        </Text>

        {/* Tab Navigation */}
        <View style={styles.tabNavigation}>
          {[
            {
              key: 'insights',
              label: 'AI Insights',
              count: filteredInsights.length,
            },
            {
              key: 'gaps',
              label: 'Skill Gaps',
              count: prioritizedSkillGaps.length,
            },
            {
              key: 'efficiency',
              label: 'Efficiency',
              count: efficiencyMetrics.suggestions.length,
            },
          ].map(tab => (
            <TouchableOpacity
              key={tab.key}
              style={[styles.tab, activeTab === tab.key && styles.activeTab]}
              onPress={() => setActiveTab(tab.key as any)}
            >
              <Text
                variant='labelLarge'
                style={[
                  styles.tabLabel,
                  activeTab === tab.key && styles.activeTabLabel,
                ]}
              >
                {tab.label}
              </Text>
              {tab.count > 0 && (
                <View style={styles.tabBadge}>
                  <Text variant='labelSmall' style={styles.tabBadgeText}>
                    {tab.count}
                  </Text>
                </View>
              )}
            </TouchableOpacity>
          ))}
        </View>

        {/* Filters */}
        {activeTab !== 'efficiency' && (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.filtersContainer}
          >
            {activeTab === 'insights' && (
              <>
                {(
                  [
                    'all',
                    'improvement',
                    'skill_gap',
                    'efficiency',
                    'trend',
                  ] as InsightFilter[]
                ).map(filter => (
                  <Chip
                    key={filter}
                    selected={insightFilter === filter}
                    onPress={() => setInsightFilter(filter)}
                    style={styles.filterChip}
                    compact
                  >
                    {filter.replace(/_/g, ' ').toUpperCase()}
                  </Chip>
                ))}
              </>
            )}

            {(['all', 'high', 'medium', 'low'] as PriorityFilter[]).map(
              filter => (
                <Chip
                  key={filter}
                  selected={priorityFilter === filter}
                  onPress={() => setPriorityFilter(filter)}
                  style={styles.filterChip}
                  compact
                >
                  {filter.toUpperCase()}
                </Chip>
              )
            )}
          </ScrollView>
        )}
      </View>

      {/* Content */}
      {renderTabContent()}
    </Surface>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    margin: 16,
    borderRadius: 12,
  },
  header: {
    padding: 16,
    paddingBottom: 8,
  },
  title: {
    marginBottom: 16,
    fontWeight: '600',
  },
  tabNavigation: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginRight: 8,
  },
  activeTab: {
    backgroundColor: 'rgba(103, 80, 164, 0.12)',
  },
  tabLabel: {
    textAlign: 'center',
  },
  activeTabLabel: {
    color: '#6750A4',
    fontWeight: '600',
  },
  tabBadge: {
    backgroundColor: '#6750A4',
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginLeft: 4,
  },
  tabBadgeText: {
    color: 'white',
    fontSize: 10,
  },
  filtersContainer: {
    flexDirection: 'row',
  },
  filterChip: {
    marginRight: 8,
  },
  tabContent: {
    flex: 1,
    paddingHorizontal: 16,
  },

  // Insight Card Styles
  insightCard: {
    marginBottom: 12,
  },
  insightHeader: {
    padding: 16,
  },
  insightHeaderContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  insightInfo: {
    flex: 1,
  },
  insightTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  insightTitle: {
    flex: 1,
    marginLeft: 8,
    fontWeight: '600',
  },
  insightDescription: {
    marginBottom: 12,
    lineHeight: 20,
  },
  insightMeta: {
    flexDirection: 'row',
    gap: 8,
  },
  impactChip: {
    height: 24,
  },
  actionableChip: {
    height: 24,
    backgroundColor: 'rgba(76, 175, 80, 0.12)',
  },
  insightDetails: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  divider: {
    marginBottom: 16,
  },
  reasoningSection: {
    marginBottom: 16,
  },
  sectionTitle: {
    marginBottom: 8,
    fontWeight: '600',
  },
  reasoningText: {
    lineHeight: 20,
    fontStyle: 'italic',
  },
  recommendationsSection: {
    marginBottom: 16,
  },
  recommendationItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    backgroundColor: 'rgba(103, 80, 164, 0.05)',
    borderRadius: 8,
    marginBottom: 8,
  },
  recommendationContent: {
    flex: 1,
  },
  recommendationTitle: {
    fontWeight: '600',
    marginBottom: 4,
  },
  recommendationDescription: {
    marginBottom: 8,
    lineHeight: 18,
  },
  recommendationMeta: {
    flexDirection: 'row',
    gap: 16,
  },
  metaText: {
    opacity: 0.7,
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  actionButton: {
    flex: 1,
  },

  // Skill Gap Styles
  gapCard: {
    marginBottom: 12,
    padding: 16,
  },
  gapHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  gapInfo: {
    flex: 1,
  },
  gapTitle: {
    fontWeight: '600',
    marginBottom: 8,
  },
  gapProgress: {
    marginBottom: 12,
  },
  progressBar: {
    height: 8,
    backgroundColor: 'rgba(0,0,0,0.1)',
    borderRadius: 4,
    position: 'relative',
    marginBottom: 4,
  },
  progressFill: {
    height: '100%',
    borderRadius: 4,
  },
  targetIndicator: {
    position: 'absolute',
    top: -2,
    width: 3,
    height: 12,
    borderRadius: 1.5,
  },
  progressText: {
    opacity: 0.7,
  },
  gapMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  priorityChip: {
    height: 24,
  },
  timeEstimate: {
    opacity: 0.7,
  },
  focusButton: {
    marginLeft: 12,
  },

  // Efficiency Styles
  efficiencyContainer: {
    gap: 16,
  },
  efficiencyCard: {
    padding: 16,
  },
  efficiencyHeader: {
    alignItems: 'center',
    marginBottom: 16,
  },
  efficiencyScore: {
    fontSize: 48,
    fontWeight: 'bold',
    color: '#4CAF50',
  },
  efficiencyBreakdown: {
    gap: 12,
  },
  metricRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  metricLabel: {
    flex: 1,
  },
  metricBar: {
    flex: 2,
    height: 8,
    backgroundColor: 'rgba(0,0,0,0.1)',
    borderRadius: 4,
  },
  metricFill: {
    height: '100%',
    borderRadius: 4,
  },
  metricValue: {
    width: 40,
    textAlign: 'right',
  },
  suggestionsContainer: {
    gap: 12,
  },
  suggestionsTitle: {
    fontWeight: '600',
  },
  suggestionCard: {
    padding: 16,
  },
  suggestionContent: {
    gap: 8,
  },
  suggestionTitle: {
    fontWeight: '600',
  },
  suggestionDescription: {
    lineHeight: 20,
  },
  suggestionMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  improvementText: {
    color: '#4CAF50',
    fontWeight: '600',
  },
  difficultyChip: {
    height: 24,
  },
});
